import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import ErrorBoundary from '@main/ErrorBoundary';
import { CurrentUserProvider } from '@contexts/CurrentUserContext';
import { Features } from '@contexts/Features/Features';
import { FeaturesProvider } from '@contexts/Features/FeaturesContext';
import { HeaderProvider } from '@contexts/HeaderContext';
import { SnackBarProvider } from '@contexts/Snackbar/SnackBarContext';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { IconContext } from '@phosphor-icons/react'; // eslint-disable-line no-restricted-imports
import AuthService from '@services/AuthService';
import { queryClient } from '@services/queryClient';
import dayjs from 'dayjs';
import { es } from 'dayjs/locale/es'; // Sin esto rompe los datepicker o corre los días de la semana del calendario
import Head from 'next/head';
import { MainRoutes, NoAuthRoutes } from '@routes';
import theme from '@theme';
import '../styles.css';
import { AbilityProvider } from '@contexts/ability/AbilityContext';
import { NotificationsProvider } from '@contexts/NotificationsContext';
dayjs.locale('es');
console.log(es);

export default function MyApp(props) {
  const { Component, pageProps } = props;
  const getLayout = Component.getLayout || ((page) => page);
  const router = useRouter();
  const isLoginRoute = Object.values(NoAuthRoutes).includes(router.pathname);
  const isAuthenticated = AuthService.isAuthenticated();

  useEffect(() => {
    // checks if the user is authenticated
    if (!isLoginRoute && !isAuthenticated && router?.isReady) {
      const redirectTo = router.asPath;
      const url = MainRoutes.login + '?redirectTo=' + redirectTo;
      globalThis.window.location.href = url;
    }
  }, [router?.isReady]);

  const iconContextValue = useMemo(
    () => ({
      color: theme.palette.primary.main,
      size: 24,
      alt: '.',
    }),
    [theme.palette.primary.main],
  );

  return (
    <>
      <Head>
        <meta name='viewport' content='initial-scale=1, width=device-width' />
        <title>{process.env.NEXT_PUBLIC_APP_NAME}</title>
        <link
          rel='apple-touch-icon'
          sizes='180x180'
          href='/apple-touch-icon.png'
        />
        <link
          rel='icon'
          type='image/png'
          sizes='32x32'
          href='/favicon-32x32.png'
        />
        <link
          rel='icon'
          type='image/png'
          sizes='16x16'
          href='/favicon-16x16.png'
        />
        <link rel='manifest' href='/site.webmanifest' />
        <link rel='mask-icon' href='/safari-pinned-tab.svg' color='#5bbad5' />
        <meta name='msapplication-TileColor' content='#da532c' />
        <meta name='theme-color' content='#ffffff' />
        <meta name='robots' content='noindex, nofollow' />
      </Head>
      <h1 style={{ display: 'none' }}>{process.env.NEXT_PUBLIC_APP_NAME}</h1>
      <div id='top-page'></div>
      <AbilityProvider>
        <ErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <SnackBarProvider>
              <HeaderProvider>
                <CurrentUserProvider>
                  <NotificationsProvider>
                    <FeaturesProvider value={Features}>
                      <LocalizationProvider
                        adapterLocale='es'
                        dateAdapter={AdapterDayjs}
                      >
                        <ReactQueryDevtools
                          initialIsOpen={false}
                          position='bottom-left'
                        />
                        <IconContext.Provider value={iconContextValue}>
                          <ThemeProvider theme={theme}>
                            <CssBaseline />
                            {getLayout(<Component {...pageProps} />)}
                          </ThemeProvider>
                        </IconContext.Provider>
                      </LocalizationProvider>
                    </FeaturesProvider>
                  </NotificationsProvider>
                </CurrentUserProvider>
              </HeaderProvider>
            </SnackBarProvider>
          </QueryClientProvider>
        </ErrorBoundary>{' '}
      </AbilityProvider>
    </>
  );
}
